import clsx from "clsx";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import React from "react";

import { CopyToClipboard } from "@/features/common/components/CopyToClipboard";
import TextPlaceholder from "@/features/placeholder/TextPlaceholder";
import { PostTag } from "@/features/post/components/common/PostTag";
import { PostDetailFragment, PostTypeEnum } from "@/generated/generated-hooks";
import { getChannelPath, getEtherscanUrl } from "@/helpers/common/path";
import { getPublicConfig } from "@/helpers/getPublicConfig";

export const NFTPostContentMenuPlaceholder = () => {
  return (
    <div className="w-full p-3 md:p-4 bg-white rounded-10 space-y-3">
      <TextPlaceholder />
      <TextPlaceholder />
      <TextPlaceholder />
    </div>
  );
};

export const NFTDetail = ({
  loading,
  tokenId,
  contractAddress,
  post,
}: {
  loading: boolean;
  tokenId: string;
  contractAddress: string;
  post: PostDetailFragment;
}) => {
  const { isProduction } = getPublicConfig();
  const { t } = useTranslation("post");

  if (loading) return <NFTPostContentMenuPlaceholder />;

  const assetContractUrl = getEtherscanUrl(contractAddress, "contract");
  const blockchain = isProduction ? "Mainnet" : "Rinkeby";

  /* classNames */
  const recordContainerClassName = "flex items-center";
  const textClassName = "text-base  text-gray-900 ml-2";
  const titleClassName = clsx(textClassName, "w-32 whitespace-nowrap shrink-0");

  return (
    <div className="w-full p-3 md:p-4 bg-white rounded-10 space-y-3">
      <div className={recordContainerClassName}>
        <span className={titleClassName}>
          {t("post:nft.detail.contractAddress")}
        </span>
        <Link href={assetContractUrl}>
          <a className="truncate" target="_blank" rel="noopener noreferrer">
            <span className={clsx(textClassName, "text-blue-500")}>
              {contractAddress}
            </span>
          </a>
        </Link>
      </div>

      <div className={recordContainerClassName}>
        <span className={titleClassName}>{t("post:nft.detail.tokenId")}</span>
        <div className="flex flex-1 min-w-0 items-center">
          <span
            className={clsx(
              textClassName,
              "flex-1 md:flex-none truncate min-w-0",
            )}
          >
            {tokenId}
          </span>
          <div className="ml-2">
            <CopyToClipboard target={tokenId} />
          </div>
        </div>
      </div>

      <div className={recordContainerClassName}>
        <span className={titleClassName}>
          {t("post:nft.detail.tokenStandard")}
        </span>
        <span className={textClassName}>{getSchema(post.postType)}</span>
      </div>

      <div className={recordContainerClassName}>
        <span className={titleClassName}>
          {t("post:nft.detail.blockchain")}
        </span>
        <span className={textClassName}>{blockchain}</span>
      </div>

      <div className={recordContainerClassName}>
        <span className={titleClassName}>{t("post:nft.detail.metadata")}</span>
        <span className={textClassName}>{t("post:nft.detail.editable")}</span>
      </div>

      <div className={recordContainerClassName}>
        <span className={titleClassName}>{t("post:channel")}</span>
        <Link
          href={getChannelPath({
            accountName: post.writer.accountName,
            channelSlug: post.channel.slug,
          })}
        >
          <a className="truncate" target="_blank">
            <span className={clsx(textClassName, "text-blue-500")}>
              {post.channel.title}
            </span>
          </a>
        </Link>
      </div>
      {post.tags.length > 0 && (
        <div className={recordContainerClassName}>
          <span className={titleClassName}>{t("post:tag")}</span>
          <div className="flex flex-wrap -ml-2 -mt-2">
            {post.tags.map((tag, idx) => (
              <div key={idx} className="ml-2 mt-2">
                <PostTag value={tag.value} label={tag.label} index={idx} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getSchema = (postType: PostTypeEnum) => {
  switch (postType) {
    case PostTypeEnum.Eth721:
    case PostTypeEnum.Nft:
      return "ERC721";
    default:
      return "";
  }
};
